import React, { useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Download, X, AlertCircle } from "lucide-react";

/**
 * PWA Update Banner - Bottom-fixed notification for available updates.
 * Accessible, keyboard-operable, respects safe areas and themes.
 */
export default function PWAUpdateBanner({
  isVisible,
  onUpdate,
  onDismiss,
  onLater,
  showDialogTrigger = false,
}) {
  const { t } = useTranslation("common");
  const bannerRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Focus management
  useEffect(() => {
    if (isVisible && bannerRef.current) {
      previousActiveElement.current = document.activeElement;
      // Focus the banner for screen reader announcement
      bannerRef.current.focus();
    } else if (!isVisible && previousActiveElement.current) {
      // Restore focus to previously focused element
      previousActiveElement.current.focus();
      previousActiveElement.current = null;
    }
  }, [isVisible]);

  // Prevent duplicate announcements
  const announcedRef = useRef(false);
  useEffect(() => {
    if (isVisible && !announcedRef.current) {
      announcedRef.current = true;
      // Announce via ARIA live region (handled by role="status")
    } else if (!isVisible) {
      announcedRef.current = false;
    }
  }, [isVisible]);

  // Keyboard handling
  const handleKeyDown = useCallback((e) => {
    if (!isVisible) return;

    const focusableElements = bannerRef.current?.querySelectorAll(
      'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    switch (e.key) {
      case "Tab":
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
        break;
      case "Escape":
        e.preventDefault();
        onLater?.();
        break;
    }
  }, [isVisible, onLater]);

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, handleKeyDown]);

  if (!isVisible) return null;

  return (
    <div
      ref={bannerRef}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="fixed bottom-0 left-0 right-0 z-50 pb-safe px-4 sm:px-6 lg:px-8 pointer-events-none animate-slide-up"
      style={{
        paddingBottom: "max(env(safe-area-inset-bottom), 1rem)",
      }}
    >
      <div
        className="pointer-events-auto mx-auto max-w-7xl transform transition-all duration-300"
        style={{ willChange: "transform, opacity" }}
      >
        <div
          className="relative flex items-center gap-3 rounded-xl bg-green-950/95 dark:bg-neutral-950/95 border border-emerald-700/50 dark:border-emerald-800/50 shadow-[0_-8px_32px_rgba(0,0,0,0.3)] backdrop-blur-xl p-3 sm:p-4"
          role="alert"
        >
          <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-600/20 text-emerald-400" aria-hidden="true">
            <AlertCircle size={18} strokeWidth={2} />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-100 dark:text-emerald-50">
              {t("pwaUpdate.bannerMessage", "A new version of Orrith is available. Tap to update")}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onUpdate}
              disabled={false}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-950 dark:text-green-50 bg-emerald-400 dark:bg-emerald-500 rounded-lg hover:bg-emerald-300 dark:hover:bg-emerald-400 active:bg-emerald-500 dark:active:bg-emerald-600 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-green-950 dark:focus:ring-offset-neutral-950 whitespace-nowrap"
              aria-label={t("pwaUpdate.updateAction", "Update")}
            >
              <Download size={14} aria-hidden="true" />
              <span>{t("pwaUpdate.updateButton", "Update")}</span>
            </button>

            {showDialogTrigger && (
              <button
                type="button"
                onClick={() => {
                  // Trigger the dialog - this will be handled by parent
                  const event = new CustomEvent("pwa-update-show-dialog");
                  window.dispatchEvent(event);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-100 dark:text-emerald-50 bg-transparent hover:bg-emerald-600/20 dark:hover:bg-emerald-500/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-green-950 dark:focus:ring-offset-neutral-950 whitespace-nowrap"
                aria-label={t("pwaUpdate.moreOptions", "More options")}
              >
                <span>{t("pwaUpdate.moreButton", "More")}</span>
              </button>
            )}

            <button
              type="button"
              onClick={onLater}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-emerald-100/70 dark:text-emerald-50/70 hover:text-emerald-100 dark:hover:text-emerald-50 hover:bg-emerald-600/15 dark:hover:bg-emerald-500/15 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-green-950 dark:focus:ring-offset-neutral-950"
              aria-label={t("pwaUpdate.laterButton", "Later")}
            >
              <X size={18} aria-hidden="true" strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}